/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router'
import {Box, Stack, Skeleton, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {usePageUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import Pagination from '@salesforce/retail-react-app/app/components/pagination'
import {DEFAULT_ORDERS_SEARCH_PARAMS} from '@salesforce/retail-react-app/app/constants'
import {remCalc} from '../../theme/_utils'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getValidToken} from '../../utils/utils'
import {BRAND_SHORT_LABEL} from '../../brand'
import SubscriptionAssistance from '../../components/subscriptions-assistance'
import SubscriptionsBox from '../../components/subscriptions-box'
import {useAccessToken} from '@salesforce/commerce-sdk-react'

const SubscriptionHistory = () => {
    const location = useLocation()
    const {data: customer} = useCurrentCustomer()
    const searchParams = useSearchParams(DEFAULT_ORDERS_SEARCH_PARAMS)
    const {limit, offset} = searchParams[0]
    const appOrigin = useOrigin(false)
    const [subscriptions, setSubscriptions] = useState([])
    const [totalSubscriptions, setTotalSubscriptions] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const pageUrls = usePageUrls({total: totalSubscriptions, limit})
    const headingRef = useRef()
    const styles = useMultiStyleConfig('Subscriptions')
    const {getTokenWhenReady} = useAccessToken()

    useEffect(() => {
        const fetchSubscriptions = async () => {
            setIsLoading(true)
            try {
                const apiUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-oms`
                const accessTokenKey = 'accessTokenOMS'
                const tokenExpirationKey = 'tokenExpirationOMS'
                const accessToken = await getValidToken(apiUrl, accessTokenKey, tokenExpirationKey)

                const queryParams = new URLSearchParams({
                    sfAccountEmail: customer.email,
                    brand: BRAND_SHORT_LABEL,
                    offset,
                })
                const token = await getTokenWhenReady()
                // Use the token from getTokenWhenReady for Commerce Cloud APIs
                // and accessToken for OMS APIs
                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-subscription-list-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`, // OMS token
                            customerId: customer?.customerId,
                            'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setSubscriptions(result.subscriptions)
                    setTotalSubscriptions(result.totalSubscriptions)
                } else {
                    setError('Failed to fetch subscription list: ' + result.error)
                }
            } catch (error) {
                console.error('Error fetching subscriptions:', error)
                setError('Error fetching subscriptions: ' + error.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (customer?.email) {
            fetchSubscriptions()
        }
    }, [customer?.email])

    const hasSubscriptions = subscriptions.length > 0

    return (
        <Stack
            gap={{base: remCalc(24), lg: remCalc(40)}}
            data-testid="account-subscription-history-page"
        >
            <Text as="h1" ref={headingRef} {...styles.accountTitle}>
                <FormattedMessage
                    defaultMessage="Subscriptions"
                    id="account_subscription_history.subscriptions_title"
                />
            </Text>

            {error && (
                <Box mb={4}>
                    <Text color="red.500" fontWeight="bold">
                        {error}
                    </Text>
                </Box>
            )}

            {!hasSubscriptions && !isLoading && (
                <Stack {...styles.noSubscriptionsContainer}>
                    <Text {...styles.noSubscriptionsTitle}>
                        <FormattedMessage
                            id="account_subscription_history.no_order_yet.title"
                            defaultMessage="No subscriptions appear in history."
                        />
                    </Text>
                    <Text {...styles.noSubscriptionsDescription}>
                        <FormattedMessage
                            id="account_subscription_history.no_order_yet.description"
                            defaultMessage="Have you purchased a subscription that should appear here? For more information, contact our customer support. If you have not yet purchased a product"
                        />
                    </Text>
                </Stack>
            )}

            {isLoading ? (
                [1, 2, 3].map((i) => (
                    <Stack key={i} spacing={4} layerStyle="cardBordered">
                        <Skeleton h="20px" w="112px" />
                        <Skeleton h="20px" w="200px" />
                    </Stack>
                ))
            ) : (
                <>
                    {hasSubscriptions && (
                        <>
                            <SubscriptionAssistance />
                            <Stack spacing={8}>
                                {subscriptions.map((subscription, i) => (
                                    <SubscriptionsBox key={i} subscription={subscription} />
                                ))}

                                {subscriptions.length < totalSubscriptions && (
                                    <Box pt={4}>
                                        <Pagination
                                            currentURL={`${location.pathname}${location.search}`}
                                            urls={pageUrls}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </>
                    )}
                </>
            )}
        </Stack>
    )
}

SubscriptionHistory.getTemplateName = () => 'account-subscription-history'

export default SubscriptionHistory
